import { CodeBlock } from '@crowdex/ui/ai/code-block';
import { ToolContent } from '@crowdex/ui/ai/tool';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@crowdex/ui/components/collapsible';
import { cn } from '@crowdex/ui/lib/utils';
import { Braces, ChevronRight, Eye, Globe, Hourglass, Search, X } from 'lucide-react';
import type {
  CodeCallKind,
  CodeStep,
  StepStatus,
} from '@/lib/conversations/message-part/code-mode';
import type { FilePart } from '@/lib/conversations/message-part/types';
import { StableTool } from './stable-tool';
import { ToolImageStrip } from './tool-image-strip';

/** A step's state in one word, said as the other steps say it. */
const STEP_STATUS_LABEL: Record<StepStatus, string> = {
  running: 'Running',
  waiting: 'Waits on you',
  failed: 'Failed',
  done: 'Done',
};

const stepStatusClass = (status: StepStatus) =>
  status === 'failed'
    ? 'text-destructive'
    : status === 'waiting'
      ? 'text-attention'
      : status === 'running'
        ? 'text-running'
        : 'text-muted-foreground';

function KindIcon({ kind, status }: { kind: CodeCallKind; status: StepStatus }) {
  const className = cn('size-3.5', status === 'running' && 'text-running');
  if (status === 'failed') return <X className="size-3.5 text-destructive" />;
  if (status === 'waiting') return <Hourglass className="size-3.5 text-attention" />;
  switch (kind) {
    case 'read':
      return <Search className={className} />;
    case 'show':
      return <Eye className={className} />;
    case 'browser':
      return <Globe className={className} />;
    case 'write':
      return <Braces className={className} />;
  }
}

const blockClass =
  'max-h-56 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-code-surface px-3 py-2 font-mono text-xs leading-5';

/**
 * A code-mode cell (`crowdex_run`) as one step: named by what its code calls ("List avatars, then
 * show Avatars"), its state in a word; opened, its operations, why it failed, what it returned
 * (its images too) and its code, folded.
 */
export function CodeStepRow({ step, images }: { step: CodeStep; images: readonly FilePart[] }) {
  const kind = step.kinds[0] ?? 'write';
  // Its operations, when its name does not already say each one ("Generate avatar photo ×3").
  const operations =
    new Set(step.operations.map((operation) => operation.title)).size > 1 ? step.operations : [];
  const value = step.result?.value ?? null;
  const output = step.result?.output ?? null;
  const error = step.status === 'failed' ? (step.result?.error ?? null) : null;
  return (
    <StableTool data-code-step={step.status}>
      <CollapsibleTrigger className="group/header flex min-h-8 w-full items-center gap-3 text-left hover:opacity-80">
        <span className="flex size-4 shrink-0 items-center justify-center text-muted-foreground">
          <KindIcon kind={kind} status={step.status} />
        </span>
        <span className="min-w-0 flex-1 truncate text-foreground text-sm">{step.title}</span>
        <span className={cn('type-meta shrink-0', stepStatusClass(step.status))}>
          {STEP_STATUS_LABEL[step.status]}
        </span>
        <ChevronRight
          aria-hidden
          className="size-3.5 shrink-0 text-muted-foreground/70 transition-transform group-data-[state=open]/header:rotate-90"
        />
      </CollapsibleTrigger>
      <ToolContent className="mb-1.5 ml-7 flex flex-col gap-2">
        {operations.length > 0 && (
          <ul className="type-meta flex flex-col gap-0.5 text-muted-foreground">
            {operations.map((operation) => (
              <li className="flex items-center gap-2" key={operation.key}>
                <span className="min-w-0 flex-1 truncate">{operation.title}</span>
                {operation.status !== null && (
                  <span className={cn('shrink-0', stepStatusClass(operation.status))}>
                    {STEP_STATUS_LABEL[operation.status]}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {error !== null && (
          <p className="type-meta whitespace-pre-wrap rounded-xl border border-destructive/20 bg-destructive/5 px-3 py-2 text-destructive">
            {error}
          </p>
        )}
        {value !== null && value !== 'null' && <pre className={blockClass}>{value}</pre>}
        {output !== null && <pre className={blockClass}>{output}</pre>}
        {images.length > 0 && <ToolImageStrip parts={images} />}
        {step.code !== null && (
          <Collapsible>
            <CollapsibleTrigger className="group/code type-meta flex items-center gap-1 text-muted-foreground hover:text-foreground">
              <ChevronRight
                aria-hidden
                className="size-3 transition-transform group-data-[state=open]/code:rotate-90"
              />
              Code
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-1">
              <CodeBlock
                className="border-hairline [&_code]:text-xs! [&_pre]:p-3! [&_pre]:text-xs!"
                code={step.code}
                language="javascript"
              />
            </CollapsibleContent>
          </Collapsible>
        )}
      </ToolContent>
    </StableTool>
  );
}
